import * as React from 'react';
import { styles, FlexCenterWrapper } from './styles';
import { handlers } from './handlers';
import { InputField, View, ViewType, EventDateType, LocationType, handleLocationInputType } from './types';
import { ANIMATION_DURATION_MS } from '@joykit/variables/animations';
import { CSSTransition } from 'react-transition-group';
import { SlideInHorizontal } from '@joykit/transitions';
import { CurrentUser, CurrentUser_me, CurrentUser_me_eventUsers } from '@graphql/types';
import { CreateEventForm, ExistingAccount, Auth } from './components';
import { authService } from '@withjoy/web-core';
import { Spinner } from '@components/Spinner';
import { PaletteHydrationState } from '../CreateWeddingHydration';
import { getCreateEventData, getDefaultTimezone, addMonths } from './util';
import { JoyHeader } from '@components/JoyHeader';
import { RouteComponentProps, withRouter } from 'react-router';
import { getLocalStorage, Analytics } from '@withjoy/web-core';
import { Box } from '@withjoy/joykit';
import { CURRENT_USER } from '@graphql/queries/user';
import { Query } from 'react-apollo';
import { ErrorPage } from '@components/Error';
import { scrollToYCasually } from 'utils/scrollToY';
import { getMarketingEnv } from 'utils/getJoyEnv';

export type CreateWeddingState = Readonly<{
  ownerFirstName: InputField;
  ownerLastName: InputField;
  partnerFirstName: InputField;
  partnerLastName: InputField;
  eventDate: EventDateType;
  location: LocationType;
  views: View;
  currentView: ViewType;
  eventUrl: string;
}>;

interface CreateWeddingProps extends RouteComponentProps {
  data?: PaletteHydrationState;
  onHeaderClick: () => void;
  inPopup?: boolean;
  popupOpen?: boolean;
  ctaText: {
    title: string;
    subtitle: string;
  };
}

interface Props extends CreateWeddingProps {
  currentUser?: CurrentUser;
  refetch: () => void;
}

const weddingStorage = getLocalStorage();
const tzData = getDefaultTimezone();
class CreateWeddingComponent extends React.Component<Props, CreateWeddingState> {
  readonly state: CreateWeddingState = {
    ownerFirstName: {
      value: '',
      error: false,
      errorText: 'Please fill out First Name',
      label: 'First Name',
      placeholder: 'Romeo',
      name: 'ownerFirstName'
    },
    ownerLastName: {
      value: '',
      error: false,
      errorText: 'Please fill out Last Name',
      label: 'Last Name',
      placeholder: 'Montague',
      name: 'ownerLastName'
    },
    partnerFirstName: {
      value: '',
      error: false,
      errorText: `Please fill out Partner's First Name`,
      label: 'Partner’s First Name',
      placeholder: 'Juliet',
      name: 'partnerFirstName'
    },
    partnerLastName: {
      value: '',
      error: false,
      errorText: `Please fill out Partner's Last Name`,
      label: 'Partner’s Last Name',
      placeholder: ' Capulet',
      name: 'partnerLastName'
    },
    eventDate: {
      value: addMonths(new Date(), 9),
      changed: false,
      notApplicable: false
    },
    location: {
      value: '',
      lat: null,
      lng: null,
      placeId: null,
      notApplicable: false,
      tzid: tzData.tzid,
      tzMethod: tzData.tzMethod,
      error: false,
      errorText: 'Please fill out Location'
    },
    currentView: 'createForm',
    views: {
      createForm: false,
      authenticate: false,
      existingAccount: false
    },
    eventUrl: ''
  };

  private timeout: number;

  private views = {
    createForm: () => {
      const { inPopup, popupOpen } = this.props;
      const { ownerFirstName, ownerLastName, partnerFirstName, partnerLastName, eventDate, location } = this.state;
      return (
        <CreateEventForm
          handleOnInput={this.handleFormInput}
          handleDate={this.handleDate}
          ownerFirstName={ownerFirstName}
          handleLocationInput={this.handleLocationInput}
          ownerLastName={ownerLastName}
          partnerFirstName={partnerFirstName}
          partnerLastName={partnerLastName}
          eventDate={eventDate}
          location={location}
          onSubmit={this.createEvent}
          handleClearForm={this.handleClearForm}
          handleNotApplicable={this.handleNotApplicable}
          ctaText={this.props.ctaText}
          handleInputErrors={this.handleInputErrors}
          inPopup={inPopup}
          popupOpen={popupOpen}
          handleTimezoneChange={this.handleTimezoneChange}
          buttonText={this.userExists() ? 'Create Event' : 'Continue'}
        />
      );
    },
    existingAccount: () => (
      <ExistingAccount
        handleLogout={this.handleLogout}
        eventUrl={this.state.eventUrl}
        currentUser={this.props.currentUser!}
        proceed={this.proceedToExistingWebsite}
      />
    ),
    authenticate: () => {
      return <Auth handleBack={this.handleGoToView} />;
    }
  };

  componentDidMount() {
    const eventData = weddingStorage.getItem('createEventJson');
    if (eventData) {
      const parsedData = JSON.parse(eventData);
      if (parsedData) {
        this.setState(handlers.hydrateStateFromLocalStorage(parsedData));
      }
    }

    if (this.props.currentUser && this.props.currentUser.me) {
      const { me } = this.props.currentUser;
      const result = this.hasExistingEvent(me);
      if (result) {
        this.handleGoToView('existingAccount', result);
      } else {
        this.handleGoToView('createForm');
      }
    } else {
      this.handleGoToView('createForm');
    }
  }

  componentDidUpdate(prevProps: Props, prevState: CreateWeddingState) {
    if (
      prevState.currentView === 'createForm' &&
      (this.state.currentView === 'existingAccount' || this.state.currentView === 'authenticate')
    ) {
      if (this.props.inPopup) {
        const popupContent = document.getElementsByClassName('slide-pane__content');
        if (popupContent[0] && popupContent[0].scrollTo) {
          popupContent[0].scrollTo(0, 0);
        }
      } else {
        scrollToYCasually(0);
      }
    }
    if (prevProps.currentUser !== this.props.currentUser) {
      if (this.props.currentUser && this.props.currentUser.me) {
        const { me } = this.props.currentUser;
        const result = this.hasExistingEvent(me);
        if (result) {
          this.handleGoToView('existingAccount', result);
        }
      }
    }
  }

  componentWillUnmount() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  createEvent = () => {
    // put stuff in local storage
    const { data, currentUser } = this.props;

    let createEventData = getCreateEventData(this.state, data);
    /* do we need to check if event data is already there? and if data props aren't 
    there should we use what is in local storage */
    weddingStorage.setItem('createEventJson', JSON.stringify(createEventData));

    // if they are already logged in, don't need to go to authenticate
    if (this.userExists() && !this.hasExistingEvent(currentUser!.me!)) {
      this.props.history.push('/createwedding/tying-the-knot');
    } else {
      this.handleGoToView('authenticate');
    }
  };

  handleFormInput = (field: string, value: string | Date, formError: boolean) => {
    this.setState(handlers.handleFormInput(field, value, formError));
  };

  handleDate = (value: Date) => {
    this.setState(handlers.handleDateInput(value));
  };

  handleInputErrorTracking = (analytics: Analytics) => {
    const { ownerFirstName, ownerLastName, partnerFirstName, partnerLastName, location } = this.state;
    const weddingDetailsFields = [ownerFirstName, ownerLastName, partnerFirstName, partnerLastName, location];
    weddingDetailsFields.forEach(field => {
      if (field.error) {
        analytics.trackError({
          attemptedAction: 'submit wedding details',
          errorDescription: `${field.errorText}`,
          error: 'CreateWeddingValidation'
        });
      }
    });
  };

  handleInputErrors = (
    ownerFirstName: InputField,
    ownerLastName: InputField,
    partnerFirstName: InputField,
    partnerLastName: InputField,
    location: LocationType,
    analytics: Analytics
  ) => {
    this.setState(
      handlers.handleSetErrors(ownerFirstName, ownerLastName, partnerFirstName, partnerLastName, location),
      () => this.handleInputErrorTracking(analytics)
    );
  };

  handleGoToView = (view: ViewType, result?: CurrentUser_me_eventUsers) => {
    this.setState(handlers.handleGoToView(view, result));
    this.timeout = window.setTimeout(() => {
      this.setState(handlers.handleShowView());
    }, 300);
  };

  hasExistingEvent = (currentUser: CurrentUser_me) =>
    currentUser &&
    currentUser.eventUsers &&
    currentUser.eventUsers.reduce((acc, curr) => (curr && curr.status === 'owner' ? curr : acc), null as any);

  handleLogout = (resetForm: boolean = true) => {
    authService.logout();
    this.props.refetch();
    if (this.state.currentView === 'existingAccount') {
      this.handleGoToView('authenticate');
    } else if (resetForm) {
      this.handleClearForm();
    }
  };

  handleLocationInput: handleLocationInputType = (location, lat, lng, placeId, tzid, formError) => {
    if (!tzid) {
      const tzInfo = getDefaultTimezone();
      tzid = tzInfo.tzid;
      this.handleTimezoneChange(tzInfo.tzMethod);
    }
    this.setState(handlers.handleLocation(location, lat, lng, placeId, tzid, formError));
  };

  handleTimezoneChange = (method: string) => {
    this.setState(handlers.handleTimezoneChange(method));
  };

  handleClearForm = () => {
    this.setState(handlers.handleClearForm());
    weddingStorage.setItem('createEventJson', JSON.stringify(null));
  };

  handleNotApplicable = (event: React.ChangeEvent<HTMLInputElement>) => {
    const field = event.currentTarget.dataset.field;
    if (field) {
      this.setState(handlers.handleNotApplicable(field));
    }
  };

  userExists = () => this.props.currentUser && this.props.currentUser.me;

  proceedToExistingWebsite = () => {
    const { eventUrl } = this.state;
    const url = `${getMarketingEnv(window.joyEnv)}/${eventUrl}/edit`;
    window.location.href = url;
  };

  renderView = () => {
    if (this.state.views[this.state.currentView]) {
      return this.views[this.state.currentView]();
    }
    return <Spinner style={{ top: '20%', height: '50%' }} />;
  };

  render() {
    const { currentUser, onHeaderClick } = this.props;
    const { currentView, views } = this.state;
    return (
      <div className={styles.createWeddingWrapper}>
        <JoyHeader>
          <JoyHeader.Left
            onClick={
              this.state.currentView === 'authenticate' ? () => this.handleGoToView('createForm') : onHeaderClick
            }
          />
          <JoyHeader.Right>
            {currentView === 'createForm' && views.createForm && this.userExists() ? (
              <span>
                Not {currentUser!.me!.email}?&nbsp;
                <a style={{ cursor: 'pointer' }} onClick={() => this.handleLogout(false)}>
                  Logout
                </a>
              </span>
            ) : null}
          </JoyHeader.Right>
        </JoyHeader>
        <FlexCenterWrapper>
          <div style={{ paddingTop: '22px', paddingLeft: '22px' }} />
          <Box paddingBottom={{ xs: 'md' }}>
            <CSSTransition
              appear={true}
              classNames={`${styles.animate} slide`}
              timeout={ANIMATION_DURATION_MS}
              in={this.state.views[this.state.currentView]}
            >
              <SlideInHorizontal from="left" to="right" as="css">
                {this.renderView()}
              </SlideInHorizontal>
            </CSSTransition>
          </Box>
          <div style={{ margin: '33px 0' }} />
        </FlexCenterWrapper>
      </div>
    );
  }
}

export const CreateWeddingWithUser: React.SFC<CreateWeddingProps> = ({ ...props }) => (
  <Query<CurrentUser> query={CURRENT_USER} notifyOnNetworkStatusChange={true} fetchPolicy={'network-only'}>
    {({ loading, error, data, refetch, networkStatus }) => {
      if (networkStatus === 4) {
        return null;
      }
      if (loading) {
        return (
          <div style={{ margin: '40px' }}>
            <JoyHeader />
            <Spinner style={{ top: '20%', height: '50%' }} id={'CURRENT_USER'} />
          </div>
        );
      }
      if (error) {
        return (
          <ErrorPage
            page="createForm"
            pagePrefix="marketing"
            category="CreateWedding"
            attemptedAction="Current User Query"
            error={error}
          />
        );
      }
      if (data) {
        return <CreateWeddingComponent refetch={refetch} {...props} currentUser={data} />;
      }
      return null;
    }}
  </Query>
);

export const CreateWedding = withRouter(CreateWeddingWithUser);
